import React, { useRef, useEffect, useState, useMemo } from 'react';
// TEMPORARY: Using SimpleChatProvider to fix Error #185
import SimpleChatProviderWrapper from './SimpleChatProvider';
import { UnifiedSSEProviderV3 } from './sse/UnifiedSSEProviderV3';
import { MockChatProvider } from './ws/MockChatProvider';
import { ChatConnectionProvider } from '../../contexts/WebSocketChatContext';
import { ConnectionStatus } from '../../contexts/ChatConnectionTypes';
import { useChatStore } from '../../stores/chatStore';

interface ChatProviderWrapperProps {
  children: React.ReactNode;
}

const USE_MOCK_CHAT = import.meta.env.VITE_ENABLE_MOCK_CHAT === 'true';
const USE_SIMPLE_PROVIDER = false; // Using fixed Zustand with stable selectors

// Global mount tracking to prevent double initialization in StrictMode
let globalMountId = 0;

export const ChatProviderWrapper: React.FC<ChatProviderWrapperProps> = ({ children }) => {
  const mountIdRef = useRef<number>(0);
  const hasLoggedRef = useRef(false);
  const [isProviderReady, setIsProviderReady] = useState(false);
  
  useEffect(() => {
    // Assign a unique mount ID to this instance
    if (mountIdRef.current === 0) {
      mountIdRef.current = ++globalMountId;
    }
    
    // Only log once per mount instance (not on every render)
    if (!hasLoggedRef.current) {
      hasLoggedRef.current = true;
      
      if (USE_SIMPLE_PROVIDER) {
        console.log(`🛡️ [Mount ${mountIdRef.current}] Using SimpleChatProvider (Error #185 fix)`);
      } else if (USE_MOCK_CHAT) {
        console.log(`🤖 [Mount ${mountIdRef.current}] Using Mock Chat Provider for local development`);
      } else {
        console.log(`🌐 [Mount ${mountIdRef.current}] Using SSE Provider for chat`);
      }
      
      // In StrictMode, we expect mount 2 to happen immediately after mount 1
      if (mountIdRef.current === 2 && process.env.NODE_ENV === 'development') {
        console.log('📝 Note: Second mount detected (React.StrictMode behavior) - this is expected in development');
      }
    }
    
    // Delay provider readiness to ensure proper initialization
    const readyTimer = setTimeout(() => {
      setIsProviderReady(true);
    }, 0);
    
    return () => {
      clearTimeout(readyTimer);
      // Cleanup on unmount if needed
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔚 [Mount ${mountIdRef.current}] ChatProviderWrapper unmounting`);
      }
    };
  }, []);
  
  // Show loading state while provider initializes
  // But only briefly - don't block forever
  if (!isProviderReady) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'var(--color-text-muted)'
      }}>
        <div>Initializing chat...</div>
      </div>
    );
  }
  
  // TEMPORARY: Use SimpleChatProvider to avoid Error #185
  if (USE_SIMPLE_PROVIDER) {
    return (
      <SimpleChatProviderWrapper>
        <ChatConnectionBridge>{children}</ChatConnectionBridge>
      </SimpleChatProviderWrapper>
    );
  }
  
  if (USE_MOCK_CHAT) {
    return (
      <MockChatProvider>
        <ChatConnectionBridge>{children}</ChatConnectionBridge>
      </MockChatProvider>
    );
  }

  // Use the fixed SSE provider with stable selectors
  return (
    <UnifiedSSEProviderV3>
      <ChatConnectionBridge>{children}</ChatConnectionBridge>
    </UnifiedSSEProviderV3>
  );
};

// Bridge Zustand chat store state into ChatConnectionContext to satisfy legacy hooks
const ChatConnectionBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const conn = useChatStore((s) => s.connectionStatus);
  const currentStream = useChatStore((s) => s.currentStream);
  const messagesById = useChatStore((s) => s.messagesById);
  const activeConvRef = useChatStore((s) => s.activeConvRef);
  const messageIdsByConvRef = useChatStore((s) => s.messageIdsByConvRef);

  const value = useMemo(() => {
    const status: ConnectionStatus = conn === 'error'
      ? ConnectionStatus.ERROR
      : conn === 'connecting'
      ? ConnectionStatus.CONNECTING
      : ConnectionStatus.CONNECTED;
    const streamText = currentStream?.draftClientId
      ? (messagesById[currentStream.draftClientId]?.content || '')
      : '';
    const messagesCount = activeConvRef ? (messageIdsByConvRef[activeConvRef]?.length || 0) : 0;
    return {
      connectionStatus: status,
      isConnected: status === ConnectionStatus.CONNECTED,
      isConnecting: status === ConnectionStatus.CONNECTING,
      hasError: status === ConnectionStatus.ERROR,
      isStreaming: currentStream?.status === 'streaming',
      currentStream: streamText,
      messagesCount,
      reconnectAttempts: 0,
      lastConnectedTime: undefined,
    };
  }, [conn, currentStream?.status, currentStream?.draftClientId, messagesById, activeConvRef, messageIdsByConvRef]);

  return <ChatConnectionProvider value={value}>{children}</ChatConnectionProvider>;
};
